import { cn } from '@data/utils/cn/cn';
import formatDuration from '@data/utils/format-duration';
import formatNumberAbbr from '@data/utils/format-number-abbr';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ComponentPropsWithoutRef } from 'react';
import { useMemo, forwardRef } from 'react';

const numberStyle = cva('act-antialiased font-normal', {
  variants: {
    size: {
      sm: 'text-sm',
      normal: 'text-base',
      md: 'text-sm laptop:text-lg',
      lg: 'text-xl',
      xl: 'text-2xl',
      xxl: 'text-3xl',
      xxxl: 'text-4xl',
      'xl-responsive': 'text-xl laptop:text-3xl',
    },
    strong: {
      true: 'font-bold text-black',
      false: 'text-neutral-900',
    },
  },
  defaultVariants: {
    size: 'normal',
    strong: false,
  },
});

export interface FormattedNumberProps
  extends Omit<ComponentPropsWithoutRef<'button'>, 'value'>,
    VariantProps<typeof numberStyle> {
  value: number | string | null | undefined;
  label?: string;
  variant?: 'scalar' | 'percent' | 'duration' | 'string';
}

export const FormattedNumber = forwardRef<
  HTMLButtonElement,
  FormattedNumberProps
>(
  (
    {
      value,
      label,
      size,
      onClick,
      className,
      variant = 'scalar',
      strong,
      ...props
    }: FormattedNumberProps,
    ref
  ) => {
    const formattedLabel = label ? ` ${label}` : '';

    const formattedValue = useMemo(() => {
      if (typeof value === 'string' && variant !== 'string') {
        throw new Error(`Cannot format string value as ${variant}`);
      }

      if (typeof value === 'string') return value;

      switch (variant) {
        case 'duration':
          return `${formatDuration(value)}${formattedLabel}`;
        case 'percent':
          return value == null || Number.isNaN(value)
            ? `-- ${formattedLabel}`
            : `${Math.round(value * 100)}%${formattedLabel}`;
        default:
          return `${formatNumberAbbr(value as number)}${formattedLabel}`;
      }
    }, [formattedLabel, variant, value]);

    if (onClick) {
      return (
        <button
          type="button"
          {...props}
          ref={ref}
          onClick={onClick}
          className={cn(
            numberStyle({ size, strong }),
            'rounded-xs text-blue-700 hover:bg-neutral-100 hover:text-blue-900',
            className
          )}
        >
          {formattedValue}
        </button>
      );
    }

    return (
      <span
        {...props}
        ref={ref}
        className={cn(numberStyle({ size, strong }), className)}
      >
        {formattedValue}
      </span>
    );
  }
);

FormattedNumber.displayName = 'FormattedNumber';
